<template>
	<div
		:class="[
			$style['base-multi-select-container'],
			{
				[$style['base-multi-select-container--multiple']]: multiple
			}
		]"
	>
		<div
			ref="textField"
			v-click-outside="inputBlurHandler"
			:style="[
				zIndex
			]"
			:class="[
				$style['base-multi-select'],
				{
					[$style['base-multi-select--disabled']]: isDisabled,
					[$style['base-multi-select--editable']]: editable && !isFocused,
					[$style['base-multi-select--with-dropdown']]: showCarret,
					[$style['base-multi-select--invalid']]: invalid,
					[$style['base-multi-select--focused']]: isFocused
				}
			]"
			@mouseenter="handleMouseEnter"
			@mouseleave="handleMouseLeave"
			@click="handleChipClick"
		>
			<div
				v-for="(chip, index) in chips"
				:key="index"
				:class="$style['chip']"
				:data-index="index"
			>
				<div :class="$style['chip__content']">
					<span>{{ chip.name }}</span>
					<span
						v-if="chip.subtitle"
						:class="$style['chip__subtitle']"
					>
						{{ chip.subtitle }}
					</span>
				</div>
				<FontAwesomeIcon
					v-if="editableFocus"
					:class="$style['chip__button']"
					:icon="['far', 'times']"
					@click="removeChip(chip)"
				/>
			</div>
			<input
				v-show="editableFocus"
				ref="input"
				v-model="input"
				:class="$style['base-multi-select__input']"
				:disabled="isDisabled"
				type="text"
				@focus="inputFocusHandler"
				@keydown="keyupHandler"
				@input="inputInputHandler"
			/>

			<FloatingLabel
				ref="label"
				:class="$style['base-multi-select__label']"
				:label-text="labelText"
				:required="isRequired"
				:is-focused="isFocused"
				:is-floated="isFloated"
			/>
			<NotchedOutline
				v-show="editableFocus"
				ref="outline"
				:class="$style['base-multi-select__outline']"
				:is-focused="isFocused"
				:is-floated="isFloated && !!labelText"
			/>
			<NotchedOutlineIdle
				v-show="!editable || isFocused"
				:class="$style['base-multi-select__idle']"
				:is-focused="isFocused"
				:is-floated="isFloated && !!labelText"
			/>

			<FontAwesomeIcon
				v-if="editable && !isFocused"
				:icon="['fal', 'pencil']"
				:class="$style['base-multi-select__edit']"
			/>

			<FontAwesomeIcon
				v-if="showCarret && !isDisabled && hasDropdownOptions"
				:icon="['fas', 'caret-down']"
				:class="[
					$style['base-multi-select__dropdown'],
					!expanded && $style['base-multi-select__dropdown--collapsed']
				]"
				@click="toggleDropdown"
			/>

			<DropdownMenu
				ref="dropdown"
				:class="$style['dropdown']"
				:handle-offset-click="false"
				@close="expanded = false"
				@offset:click="handleDropdownOffsetClick"
			>
				<small
					v-if="loading"
					:class="$style['subtitle']"
				>
					{{ $tc('common.messages.loading', 2) }}
				</small>
				<ul
					v-else
					:class="$style['dropdown__list']"
				>
					<li
						v-for="(item, index) in filteredList"
						:key="index + item.name"
						:class="[
							$style['dropdown__list__item'],
							{ [$style['dropdown__list__item--active']]: activeItem === index }
						]"
						@mouseover="activeItem = index"
						@click="addChip(item)"
					>
						<div
							v-if="showChildrenCount"
							:class="$style['dropdown__list__item__icon']"
						>
							<FontAwesomeIcon
								v-if="item.children && item.children.length"
								:icon="['fal', 'folder-open']"
							/>
						</div>
						<div :class="$style['dropdown__list__item__content']">
							<span>{{ item.name }}</span>
							<span
								v-if="item.subtitle"
								:class="$style['subtitle']"
							>
								{{ item.subtitle }}
							</span>
						</div>
						<span
							v-if="item.children && item.children.length && showChildrenCount"
							:class="$style['dropdown__list__item__childrens']"
						>
							{{ item.childCount }}
						</span>
					</li>
					<template v-if="additionalFields">
						<li
							v-for="(item, index) in additionalFields"
							:key="index+filteredList.length"
							:class="[
								$style['dropdown__list__item'],
								$style['dropdown__list__item--additional']
							]"
							@mouseover="activeItem = index+filteredList.length"
							@click="handleAditionalLinkClick($event, item)"
						>
							<FontAwesomeIcon
								v-if="item.icon"
								:icon="item.icon"
							/>
							{{ item.title }}
						</li>
					</template>
				</ul>
				<small
					v-if="showNoResults && !!input && !filteredList.length && !loading"
					:class="$style['subtitle']"
				>
					{{ $t('common.messages.no-results') }}
				</small>
			</DropdownMenu>
		</div>
		<p
			v-if="helperText"
			:class="[
				$style['base-multi-select-helper-text'],
				invalid && $style['base-multi-select-helper-text--invalid']
			]"
		>
			{{ helperText }}
		</p>
	</div>
</template>

<script>
function normalize (phrase) {
	if (!phrase) return
	return phrase.normalize('NFKD').replace(/[^\w\s!-}\/]/g, '')
}

import NotchedOutline from '@c:essentials/NotchedOutline'
import NotchedOutlineIdle from '@c:essentials/NotchedOutlineIdle'
import FloatingLabel from '@c:essentials/FloatingLabel'
import DropdownMenu from '@c:essentials/DropdownMenu'
import requiredField from '@/mixins/requiredField'

export default {
	name: 'BaseDropdownChips',
	components: {
		NotchedOutline,
		NotchedOutlineIdle,
		FloatingLabel,
		DropdownMenu
	},
	mixins: [requiredField],
	inheritAttrs: false,

	model: {
		prop: 'model',
		event: 'input'
	},

	props: {
		editable: {
			type: Boolean,
			default: false
		},
		additionalFields: {
			type: Array,
			default: () => []
		},
		loading: {
			type: Boolean,
			default: false
		},
		showCarret: {
			type: Boolean,
			default: true
		},
		showNoResults: {
			type: Boolean,
			default: false
		},
		avoidFiltering: {
			type: [Boolean, String],
			default: false
		},
		list: {
			type: Array,
			default: () => []
		},
		model: {
			type: Array,
			default: () => []
		},
		labelText: {
			type: String,
			default: null
		},
		required: {
			type: [Boolean, String],
			default: false
		},
		helperText: {
			type: String,
			default: null
		},
		multiple: {
			type: [Boolean, String],
			default: false
		},
		invalid: {
			type: [Boolean, String],
			default: false
		},
		showChildrenCount: {
			type: [String, Boolean],
			default: false
		}
	},

	data: () => ({
		isFocused: false,
		isFloated: false,
		input: null,
		chipsLocal: [],
		showFullList: false,
		activeItem: -1,
		isHovered: false,
		expanded: false,
		filterQuery: null
	}),

	computed: {
		editableFocus () {
			if (this.editable) {
				return this.isFocused
			}
			return true
		},
		chips: {
			get () {
				return this.chipsLocal
			},
			set (updated) {
				this.chipsLocal = updated
			}
		},
		suggestion () {
			return this.input ? { ...this.list.find(item => {
				return item.name.toLowerCase().startsWith(this.input.toLowerCase())
			}) } : { name: null, id: null }
		},
		isDisabled () {
			return this.$attrs.disabled === '' || this.$attrs.disabled
		},
		hasDropdownOptions () {
			return this.list.length > 0 || this.additionalFields.length > 0 || this.loading
		},
		zIndex () {
			if (this.isFocused) {
				return {
					'z-index': '12'
				}
			}
			return {
				'z-index': '1'
			}
		},
		filteredList () {
			if (this.avoidFiltering || this.showFullList || !this.filterQuery) {
				return this.list
			}
			const normalizedQuery = normalize(this.filterQuery)
			if (!normalizedQuery) {
				return this.list
			}
			const query = normalizedQuery.split(' ')
			const result = query.reduce((res, _item) => {
				return `${res}(?=.*${_item.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`
			}, '')
			const arrayOfQueries = new RegExp(result, 'i')
			const loweredQuery = normalizedQuery.toLowerCase()
			const filtered = []
			for (const item of this.list) {
				if (!item || !item.name) continue
				const normalizedName = normalize(item.name)
				if (!arrayOfQueries.test(normalizedName)) continue
				if (normalizedName.toLowerCase().startsWith(loweredQuery)) {
					filtered.push({ ...item, weight: 1 })
				} else {
					filtered.push(item)
				}
			}
			return filtered.sort((a, b) => {
				const nameA = (a.name || '').toUpperCase()
				const nameB = (b.name || '').toUpperCase()

				if (a.weight || b.weight) {
					if ((a.weight || 0) > (b.weight || 0)) {
						return -1
					} else {
						return 1
					}
				}

				if (nameA < nameB) {
					return -1
				}
				if (nameA > nameB) {
					return 1
				}
				return 0
			})
		}
	},
	watch: {
		model: {
			immediate: true,
			handler (newVal) {
				this.chips = Array.isArray(newVal) ? [...newVal] : []
				if (this.chips.length > 0) {
					this.isFloated = true
				}
				this.$nextTick(() => {
					this.syncOutlineHoverStyles()
				})
			}
		},
		isHovered () {
			this.$nextTick(() => {
				this.syncOutlineHoverStyles()
			})
		},
		isFocused (newVal) {
			if (newVal) {
				this.showFullList = true
			}
			this.$nextTick(() => {
				this.syncOutlineHoverStyles()
			})
		},
		isDisabled () {
			this.$nextTick(() => {
				this.syncOutlineHoverStyles()
			})
		},
		invalid () {
			this.$nextTick(() => {
				this.syncOutlineHoverStyles()
			})
		}
	},

	created () {
		if (this.editable) {
			this.isFloated = true
		}
		if (this.model.length > 0) {
			this.chips = [...this.model]
		}
		if (this.chips.length > 0) {
			this.isFloated = true
		}
	},
	mounted () {
		this.syncOutlineHoverStyles()
	},
	methods: {
		syncOutlineHoverStyles () {
			const outline = this.$refs.outline && this.$refs.outline.$el
			const idle = this.$refs.idle && this.$refs.idle.$el

			if (!outline || !idle) {
				return
			}

			const path = outline.querySelector('svg path')

			if (this.isDisabled) {
				if (path) {
					path.style.stroke = ''
				}
				idle.style.borderColor = ''
				return
			}

			if (this.invalid) {
				if (path) {
					path.style.stroke = 'var(--danger)'
				}
				idle.style.borderColor = 'var(--danger)'
				return
			}

			if (this.isHovered && !this.isFocused) {
				if (path) {
					path.style.stroke = 'var(--text)'
				}
				idle.style.borderColor = 'var(--text)'
				return
			}

			if (path) {
				path.style.stroke = ''
			}
			idle.style.borderColor = ''
		},

		addChip (chip) {
			if (!!chip.name && !this.chips.find(_chip => {
				return chip.name === _chip.name
			})) {
				this.chips = [...this.chips, chip]
				this.input = null
				this.filterQuery = null
				this.$emit('input', this.chipsLocal)
				this.isFloated = true
				this.$refs['dropdown'].close()
			}
		},

		removeChip (chipToRemove) {
			this.chips = this.chips.filter(chip => chip !== chipToRemove)
			if (this.chipsLocal.length === 0 && !this.isFocused) {
				this.isFloated = false
			}
			this.$emit('input', this.chipsLocal)
		},

		removeBack () {
			if (this.chips.length) {
				if (this.input === '' || this.input === null) {
					const updatedChips = [...this.chips]
					this.input = updatedChips.pop().name
					this.filterQuery = this.input
					this.showFullList = false
					this.chips = updatedChips
					this.$emit('input', this.chipsLocal)
				}
			}
		},

		handleAditionalLinkClick (e, link) {
			this.$refs['dropdown'].close()
			link.click(e)
		},

		keyupHandler (event) {
			if (event.key === 'Enter') {
				this.addChip(this.suggestion)
			} else if (event.keyCode === 46 || event.keyCode === 8) {
				this.removeBack()
			} else if (event.key === 'Esc') {
				event.preventDefault()
				this.$refs['dropdown'].close()
				this.$emit('change', this.input)
			} else if (event.keyCode === 40) {
				this.inputFocusHandler()
				this.$refs['dropdown'].open()
				if (this.filteredList.length - 1 > this.activeItem) {
					this.activeItem = this.activeItem + 1
					this.input = this.filteredList[this.activeItem].name

					const element = this.$el.getElementsByClassName(this.$style['dropdown__list__item--active'])[0]
					if (element) {
						element.parentNode.scrollTop = element.offsetTop + 40
					}
				}
			} else if (event.keyCode === 38) {
				this.inputFocusHandler()
				if (this.activeItem > 0) {
					this.activeItem = this.activeItem - 1
					this.input = this.filteredList[this.activeItem].name
					const element = this.$el.getElementsByClassName(this.$style['dropdown__list__item--active'])[0]
					if (element) {
						element.parentNode.scrollTop = element.offsetTop - 40
					}
				} else {
					this.$refs['dropdown'].close()
					this.activeItem = -1
				}
			} else {
				this.$refs['dropdown'].open()
			}
		},

		inputInputHandler (event) {
			this.showFullList = false
			this.activeItem = -1
			this.filterQuery = event.target.value
			this.$emit('input-update', event.target.value)
		},

		inputFocusHandler (event) {
			this.$refs['dropdown'].open()
			this.isFocused = true
			this.isFloated = true
			this.expanded = true
			this.$emit('focus', event)
			this.syncOutlineHoverStyles()
		},

		inputBlurHandler (_event) {
			this.$refs['dropdown'].close()
			this.expanded = false
			this.showFullList = false
			this.isHovered = false
			this.input = null
			this.filterQuery = null
			this.isFocused = false
			if (!this.chips.length && !this.editable) {
				this.isFloated = false
			}
			this.$emit('blur', this.chipsLocal)
		},

		handleDropdownOffsetClick (e) {
			if (this.$refs.textField && this.$refs.textField.contains(e.target)) return
			this.$refs['dropdown'].close()
		},

		toggleDropdown (e) {
			if (this.isDisabled) return
			this.showFullList = true
			if (this.expanded) {
				this.$refs['dropdown'].close()
				return
			}
			this.$refs['dropdown'].open()
			this.expanded = true
			this.inputFocusHandler(e)
			this.$nextTick(() => {
				this.$refs.input.focus()
			})
		},

		validateField () {
			const fieldName = this.$attrs['data-vv-name'] || this.$attrs.name

			if (!fieldName || !this.$validator) {
				return
			}

			this.$nextTick(() => {
				this.$validator.validate(fieldName)
			})
		},

		handleChipClick () {
			if (this.isDisabled) {
				return
			}
			if (this.editable && !this.isFocused) {
				this.inputFocusHandler()
				this.$nextTick(() => {
					this.$refs.input.focus()
				})
			}
		},

		handleMouseEnter () {
			if (!this.isDisabled) {
				this.isHovered = true
			}
		},

		handleMouseLeave () {
			this.isHovered = false
		}
	}
}
</script>

<style lang="scss" module>
.subtitle {
    color: getColor(caption);
    padding: 10px 0 10px 20px;
    display: block;
}

.chip {
    z-index: 1;
    background-color: getColor(chip);
    color: getColor(text);
    display: inline-flex;
    padding: 7px 12px;
    border: 1px solid;
    border-color: getColor(border);
    border-radius: 5px;
    align-items: center;
    cursor: default;
    min-height: 30px;
    height: 1px;
    margin: 2.5px;
    font-size: 14px;

    &__button {
        z-index: 1;
        margin-top: 2px;
        width: 12px;
        height: 15px;
        font-size: 12px;
        color: getColor(grey);
        margin-left: 8px;
        margin-right: 0;

        &:hover {
            opacity: 0.7;
            cursor: pointer;
        }
    }
}

.base-multi-select-container {
    width: 100%;

    &--multiple {
        padding: 0 15px;
    }
}

.base-multi-select-helper-text {
    position: relative;
    bottom: 10px;
    margin: 0 $gutter;
    color: getColor(helper-text);
    font-size: 12px;
    line-height: 1.25rem;
}

.base-multi-select {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    position: relative;
    min-height: 48px;
    margin: $gutter * 1.25 0;
    padding: 10px;

    &__idle {
        background-color: getColor(input-background);
    }

    &__outline {
        background-color: getColor(input-background);
    }

    &:not(.base-multi-select--invalid, .base-multi-select--focused):hover {
        .app-text-field__outline {
            border-color: getColor(text);
        }

        .app-text-field__idle {
            border-color: getColor(text);
        }
    }

    .app-text-field__input {
        color: getColor(text);
    }

    &--with-dropdown {
        .base-multi-select__input {
            padding-right: 25px !important;
        }
    }

    &--editable {
        &:hover {
            cursor: pointer;

            &::before {
                content: "";
                background-color: getColor(mark);
                width: 100%;
                top: 5px;
                bottom: 5px;
                position: absolute;
            }

            .base-multi-select__edit {
                opacity: 1;
            }
        }
    }

    &--invalid {
        .base-multi-select__input {
            caret-color: getColor(error);
        }

        .app-text-field__outline {
            border-color: getColor(error) !important;
        }

        .app-text-field__idle {
            border-color: getColor(error) !important;
        }

        .base-multi-select__label {
            color: getColor(error) !important;
        }

        & ~ .base-multi-select-helper-text {
            color: getColor(error) !important;
        }
    }

    &--disabled {
        .base-multi-select__idle {
            background-color: getColor(input-background-disabled);
        }

        .base-multi-select__outline {
            background-color: getColor(input-background-disabled);
        }

        .app-text-field__label {
            color: getColor(input-label-disabled);
        }

        .base-multi-select__input {
            color: getColor(disabled-text);
        }

        .chip {
            opacity: 0.6;
        }
    }

    &__label {
        z-index: 1;
    }

    &__input {
        z-index: 2;
        min-width: 100px;
        flex: 1;
        margin-left: 10px;
        height: 100%;
        outline: 0;
        font-size: 16px;
        color: getColor(text);
        background-color: transparent;
        border: 0;
        width: 100%;
    }

    &__edit {
        position: absolute;
        z-index: 2;
        opacity: 0;
        right: 0;
        font-size: 18px;
    }

    &__dropdown {
        z-index: 2;
        height: 100%;
        width: 30px;
        position: absolute;
        background-position: right 15px center;
        background-repeat: no-repeat;
        cursor: pointer;
        right: 0;
        color: getColor(grey);

        // Use CSS mask for theme-adaptive icon
        mask-image: url("data:image/svg+xml;charset=utf-8,%3Csvg width='10' height='5' viewBox='7 10 10 5' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
        mask-position: right 15px center;
        mask-repeat: no-repeat;
        background-color: currentcolor;
        transform: rotate(-180deg);
        transform-origin: 10px center;
        transition: transform 0.2s ease-in-out;

        &--collapsed {
            transform: rotate(0);
        }

        &:hover {
            cursor: pointer;
        }
    }
}

.dropdown {
    position: absolute;
    background-color: getColor(surface-elevated);
    transform-origin: top;
    left: 0;
    right: 0;
    top: 100%;
    z-index: 999;
    border-radius: 8px;

    @include dark-mode {
        border: 1px solid transparent;
        box-shadow: 0 0 0 1px getColor(border-darker), 0 8px 24px getColor(shadow), 0 2px 8px getColor(shadow-light);
    }

    &__list {
        list-style: none;
        padding: 0;
        margin: 0;
        overflow: auto;
        max-height: 200px;

        &__item {
            font-size: 14px;
            padding: $gutter $gutter * 2;
            display: flex;
            justify-content: flex-start;
            transition: background-color 0.12s ease;

            &__icon {
                height: 20px;
                width: 20px;
                margin-right: 5px;
                display: flex;
                align-items: center;
            }

            &__childrens {
                margin-left: auto;
                color: getColor(caption);
            }

            &--active {
                cursor: pointer;
                background-color: getColor(hover-overlay);

                @include dark-mode {
                    background-color: getColor(hover-overlay-strong);
                }
            }

            &--additional {
                box-shadow: 0 -1px 2px 0 getColor(hover-overlay);
                color: getColor(tertiary);

                &:hover {
                    cursor: pointer;
                }
            }
        }
    }
}
</style>
